import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../services/apiService";
import { fetchCsrfToken, clearCsrfToken } from "../services/csrfService";
import { fetchUserCommunities } from "../services/communityService";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userCommunities, setUserCommunities] = useState([]);
    const [activeCommunity, setActiveCommunity] = useState(null); // Kept for backward compatibility
    const [loading, setLoading] = useState(true);
    const [initialized, setInitialized] = useState(false);
    const [authState, setAuthState] = useState({ action: 'initial', timestamp: Date.now() });

    // First, fetch CSRF token and check for existing session
    useEffect(() => {
        const initializeAuth = async () => {
            try {
                // Ensure we have a CSRF token to start with (only fetch once)
                // The token will automatically be stored in a cookie and used by apiService
                await fetchCsrfToken();
                
                // Check if we have a session by making a request to the profile endpoint
                try {
                    const response = await api.get('/profile');
                    if (response.data && typeof response.data === "object") {
                        setUser(response.data);
                        
                        // Fetch user's communities
                        await loadUserCommunities();
                    }
                } catch (error) {
                    // Clear any stale data
                    setUser(null);
                    setUserCommunities([]);
                    setActiveCommunity(null);
                    localStorage.removeItem("authUser");
                }
            } catch (error) {
                // Error in initial auth setup
            } finally {
                setLoading(false);
                setInitialized(true);
            }
        };

        initializeAuth();
    }, []);
    
    // Load the user's communities
    const loadUserCommunities = async () => {
        try {
            const communities = await fetchUserCommunities();

            if (communities && communities.length > 0) {
                setUserCommunities(communities);
                
                // Find the primary community and set it as active (for backward compatibility)
                const primaryCommunity = communities.find(c => c.is_primary);
                if (primaryCommunity) {
                    setActiveCommunity(primaryCommunity);
                } else {
                    // If no primary, use the first one
                    setActiveCommunity(communities[0]);
                }
            }
        } catch (error) {
            // Error loading user communities
        }
    };

    // Fetch user profile when auth state changes (login/logout actions)
    useEffect(() => {
        const fetchUser = async () => {
          // If the current auth action is logout, skip fetching the profile
          if (authState.action === 'logout') {
            setUser(null);
            setUserCommunities([]);
            setActiveCommunity(null);
            localStorage.removeItem("authUser");
            return;
          }

          try {
            const response = await api.get('/profile');

            if (response.data && typeof response.data === "object") {
              setUser(response.data);
              // Store minimal user info in localStorage for UI persistence
              localStorage.setItem("authUser", JSON.stringify({
                email: response.data.email,
                displayName: response.data.display_name,
                role: response.data.role
              }));
              
              // After setting user, load their communities
              await loadUserCommunities();
              setUser(prevUser => ({
                ...prevUser,
                communities: userCommunities
              }));
            } else {
              setUser(null);
              setUserCommunities([]);
              setActiveCommunity(null);
              localStorage.removeItem("authUser");
            }
          } catch (error) {
            setUser(null);
            setUserCommunities([]);
            setActiveCommunity(null);
            localStorage.removeItem("authUser");
          }
        };
      
        if (authState.action !== 'initial') {
          fetchUser();
        }
      }, [authState]);

    const login = async (userData) => {
        // Set the user with data from login response for immediate UI update
        setUser(userData);
        
        // Store minimal user info in localStorage for UI persistence
        localStorage.setItem("authUser", JSON.stringify({
            email: userData.email,
            displayName: userData.display_name,
            role: userData.role
        }));
        
        // Then trigger a profile fetch to get the complete user data
        setAuthState({ action: 'login', timestamp: Date.now() });
    };

    const logout = async () => {
        setUser(null);
        setUserCommunities([]);
        setActiveCommunity(null);
        
        // Clear CSRF token from cookie
        clearCsrfToken();
        
        localStorage.removeItem("authUser");
        setAuthState({ action: 'logout', timestamp: Date.now() });
    };

    // Add a refreshUser method to manually refresh user data
    const refreshUser = async () => {
        try {
            // Fetch user profile - the interceptor will handle CSRF token if needed
            const response = await api.get('/profile');
            
            if (response.data && typeof response.data === "object") {
                setUser(response.data);
                
                // Update localStorage
                localStorage.setItem("authUser", JSON.stringify({
                    email: response.data.email,
                    displayName: response.data.display_name,
                    role: response.data.role
                }));
                
                // Refresh communities
                await loadUserCommunities();
                
                return response.data;
            }
            return null;
        } catch (error) {
            return null;
        }
    };

    // Refresh the CSRF token - useful for components that need a fresh token
    const refreshCsrfToken = async () => {
        try {
            return await fetchCsrfToken();
        } catch (error) {
            return null;
        }
    };
    
    // Change active community - kept for backward compatibility
    const switchCommunity = (communityId) => {
        const community = userCommunities.find(c => c.id === communityId);
        if (community) {
            setActiveCommunity(community);
            return true;
        }
        return false;
    };
    
    // New functions to better support the multi-community model
    
    // Get all community IDs the user belongs to
    const getUserCommunityIds = () => {
        return userCommunities.map(c => c.id);
    };
    
    // Check if user is admin of a specific community
    const isAdminOfCommunity = (communityId) => {
        if (!userCommunities || !communityId) return false;
        
        // Check if global admin (admin of all communities)
        if (user && user.role === 'global_admin') return true;
        
        // Find the community and check role
        const community = userCommunities.find(c => c.id === parseInt(communityId));
        return community && (community.role === 'community_admin' || community.role === 'admin');
    };
    
    // Check if user is a member of a specific community
    const isMemberOfCommunity = (communityId) => {
        if (!userCommunities || !communityId) return false;
        
        // Global admins are considered members of all communities
        if (user && user.role === 'global_admin') return true;
        
        // Check if user is a member of this community
        return userCommunities.some(c => c.id === parseInt(communityId));
    };

    // Helper to get user's auth status
    const isAuthenticated = user !== null;

    // Don't render children until we've initialized
    if (!initialized) {
        return <div className="text-center my-5">Initializing application...</div>;
    }

    return (
        <AuthContext.Provider value={{ 
            user, 
            login, 
            logout, 
            loading, 
            refreshCsrfToken,
            refreshUser, // New method to refresh user data
            userCommunities,
            activeCommunity, // Kept for backward compatibility
            switchCommunity, // Kept for backward compatibility
            getUserCommunityIds, // New function for getting all community IDs
            isAdminOfCommunity,
            isMemberOfCommunity,
            isAuthenticated // Helper to easily check authentication status
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);